import React, { useState, useRef, useEffect } from 'react';

interface InfoTooltipProps {
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export function InfoTooltip({ text, position = 'top', className = '' }: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    timerRef.current = setTimeout(() => setIsVisible(true), 200);
  };

  const hideTooltip = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Adjust position if tooltip would go off-screen
  useEffect(() => {
    if (isVisible && tooltipRef.current && iconRef.current) {
      const tooltip = tooltipRef.current.getBoundingClientRect();
      const icon = iconRef.current.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      let newPosition = position;

      // Check if tooltip goes off-screen and adjust
      if (position === 'top' && tooltip.top < 0) {
        newPosition = 'bottom';
      } else if (position === 'bottom' && tooltip.bottom > viewport.height) {
        newPosition = 'top';
      } else if (position === 'left' && tooltip.left < 0) {
        newPosition = 'right';
      } else if (position === 'right' && tooltip.right > viewport.width) {
        newPosition = 'left';
      }

      if (newPosition !== adjustedPosition) {
        setAdjustedPosition(newPosition);
      }
    }
  }, [isVisible, position, adjustedPosition]);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2',
    bottom: 'bottom-full left-1/2 -translate-x-1/2',
    left: 'left-full top-1/2 -translate-y-1/2',
    right: 'right-full top-1/2 -translate-y-1/2',
  };

  return (
    <span
      ref={iconRef}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      tabIndex={0}
    >
      {/* Info icon - question mark in circle with Keder yellow */}
      <svg
        className="w-4 h-4 text-amber-500 hover:text-amber-600 cursor-help transition-colors"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>

      {/* Tooltip - Keder branded (light theme) */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 ${positionClasses[adjustedPosition]}`}
          role="tooltip"
        >
          <div
            className="bg-gray-900 border border-amber-500/30 text-white text-[13px] px-4 py-3 rounded-lg shadow-xl max-w-[300px] whitespace-normal"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <div className="flex items-start gap-2">
              <span className="text-amber-400 text-xs mt-0.5">💡</span>
              <span className="leading-relaxed">{text}</span>
            </div>
          </div>
          {/* Arrow */}
          <div
            className={`absolute w-0 h-0 border-[6px] ${arrowClasses[adjustedPosition]}`}
            style={{
              borderTopColor: adjustedPosition === 'top' ? '#111827' : 'transparent',
              borderBottomColor: adjustedPosition === 'bottom' ? '#111827' : 'transparent',
              borderLeftColor: adjustedPosition === 'left' ? '#111827' : 'transparent',
              borderRightColor: adjustedPosition === 'right' ? '#111827' : 'transparent',
            }}
          />
        </div>
      )}
    </span>
  );
}

export default InfoTooltip;
